// frontend/src/components/InviteRegistration.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../axiosConfig';
import { toast } from 'react-toastify';
import { Eye, EyeOff, Info } from 'lucide-react';
import ExistingUserJoin from './ExistingUserJoin';
import AuthLanguageSelector from './AuthLanguageSelector';
import referenceService from '../services/referenceService';
import { isInternationalNumber } from '../utils/phoneUtils';

const getErrorLogSummary = (error) => ({
  status: error?.response?.status,
  code: error?.response?.data?.error,
  message: error?.message
});

export const isListedTimeZoneOption = (timeZones, value) => (
  Array.isArray(timeZones)
  && timeZones.some(timeZone => timeZone?.iana_name === value)
);

/**
 * InviteRegistration Component
 * Handles user registration via invitation token
 * Supports both join_entity and create_entity invitation types
 */
const InviteRegistration = ({ setToken }) => {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '', // For QR code invitations
    // Entity creation fields (only for create_entity invitations)
    entityName: '',
    organizationType: 'company',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'CA',  // Default to Canada ISO code
    entityPhone: '',
    timeZone: ''
  });

  // Reference data from database
  const [countries, setCountries] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [loadingReferenceData, setLoadingReferenceData] = useState(true);

  // Invitation state
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false
  });
  
  // Existing user state
  const [existingUser, setExistingUser] = useState(null);
  const [showExistingUserFlow, setShowExistingUserFlow] = useState(false);
  
  // QR Code invitation state
  const [isQRCodeInvitation, setIsQRCodeInvitation] = useState(false);

  // Load reference data when component mounts
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        setLoadingReferenceData(true);
        const locationData = await referenceService.getLocationData({ allowStaticFallback: false });
        setCountries(locationData.countries);
        setTimezones(locationData.timezones);
      } catch (error) {
        console.error('Error loading reference data:', getErrorLogSummary(error));
        toast.error(t('failedToLoadLocationData'));
      } finally {
        setLoadingReferenceData(false);
      }
    };
    
    loadReferenceData();
  }, []);

  // Load invitation details when component mounts
  useEffect(() => {
    validateInvitation();
  }, [inviteToken]);

  // Validate QR code with email when email is entered (debounced)
  useEffect(() => {
    if (isQRCodeInvitation && formData.email.trim()) {
      const timeoutId = setTimeout(() => {
        validateQRCodeWithEmail(formData.email.trim());
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [formData.email, isQRCodeInvitation]);

  /**
   * Validate invitation token and load invitation details
   * First tries standard validation, then falls back to generic QR code validation
   */
  const validateInvitation = async (email = null) => {
    try {
      setLoading(true);
      
      if (!inviteToken) {
        toast.error(t('invalidInvitationLink'));
        navigate('/login');
        return;
      }

      // First, try standard email-based invitation validation
      try {
        const response = await axios.get(
          `/api/entities/invites/validate/${inviteToken}`,
          { skipAuthSessionHandling: true }
        );
        if (response.data.valid) {
          setInvitation(response.data.invitation);
          response.data.invitation.email? setIsQRCodeInvitation(false): setIsQRCodeInvitation(true);
          
          // Handle existing user detection
          if (response.data.userStatus === 'existing_user') {
            setExistingUser(response.data.existingUser);
            setShowExistingUserFlow(true);
          } else if (response.data.userStatus === 'already_member') {
            toast.error(t('alreadyMemberOrganization'));
            navigate('/login');
            return;
          }
          
          setLoading(false);
          return; // Successfully validated as standard invitation
        }
      } catch {
        // Standard validation failed, try generic QR code validation
      }

      // If we reach here, standard validation failed - try generic QR code validation
      if (email) {
        // We have an email, try generic validation
        try {
          const genericResponse = await axios.get(
            `/api/entities/invites/validate-generic/${inviteToken}?email=${encodeURIComponent(email)}`,
            { skipAuthSessionHandling: true }
          );
          
          if (genericResponse.data.valid) {
            setInvitation(genericResponse.data.invitation);
            setIsQRCodeInvitation(true);
            
            setLoading(false);
            return; // Successfully validated as QR code invitation
          } else {
            // Generic validation also failed
            handleValidationError(genericResponse.data);
          }
        } catch (genericError) {
          console.error('Generic validation failed:', getErrorLogSummary(genericError));
          handleValidationError(genericError.response?.data || {});
        }
      } else {
        // No email provided - this is a QR code or SMS, proceed to registration form
        setIsQRCodeInvitation(true);
        // Set a placeholder invitation for QR codes
        setInvitation({
          role: 'user', // Default role, will be updated when email is provided
          invitationType: 'join_entity',
          entityName: t('loading'), // Will be updated when email is validated
          entityId: null
        });
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Error validating invitation', getErrorLogSummary(error));
      toast.error(t('unableToValidateInvitation'));
      navigate('/login');
    }
  };

  /**
   * Handle validation errors with appropriate user messages
   */
  const handleValidationError = (errorData) => {
    switch (errorData?.error) {
      case 'INVITE_NOT_FOUND':
        toast.error(t('invitationNotFound'));
        break;
      case 'INVITE_EXPIRED':
        toast.error(t('invitationExpired'));
        break;
      case 'INVITE_ALREADY_USED':
        toast.error(t('invitationAlreadyUsed'));
        break;
      case 'NOT_GENERIC_INVITATION':
        toast.error(t('invitationSpecificEmail'));
        break;
      case 'USAGE_LIMIT_EXCEEDED':
        toast.error(t('qrUsageLimitExceeded'));
        break;
      default:
        toast.error(errorData?.message || t('invalidInvitation'));
    }
    navigate('/login');
  };

  /**
   * Validate QR code invitation with user-provided email
   */
  const validateQRCodeWithEmail = async (email) => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setValidationErrors(prev => ({
        ...prev,
        email: t('validation.emailInvalidFormat')
      }));
      return;
    }

    // Clear email validation error
    setValidationErrors(prev => ({
      ...prev,
      email: ''
    }));

    try {
      const response = await axios.get(
        `/api/entities/invites/validate-generic/${inviteToken}?email=${encodeURIComponent(email)}`,
        { skipAuthSessionHandling: true }
      );
      
      if (response.data.valid) {
        // Update invitation data with real information
        setInvitation(response.data.invitation);
        // Generic QR preview intentionally returns only invitation metadata.
        // Existing users choose the password-verified join path explicitly so
        // public preview cannot disclose account or membership state.
      } else {
        // Handle validation errors without navigating away
        setValidationErrors(prev => ({
          ...prev,
          email: response.data.message || t('invalidEmailForInvitation')
        }));
      }
    } catch (error) {
      console.error('QR code email validation failed:', getErrorLogSummary(error));
      if (error.response?.data?.error) {
        setValidationErrors(prev => ({
          ...prev,
          email: error.response.data.message || t('validation.unableToValidateEmail')
        }));
      }
    }
  };

  /**
   * Handle form field changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Validate form fields
   */
  const validateForm = () => {
    const errors = {};

    // Username validation
    if (!formData.username.trim()) {
      errors.username = t('validation.usernameRequired');
    } else if (formData.username.length < 3) {
      errors.username = t('validation.usernameMinLength');
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      errors.username = t('validation.usernameInvalidChars');
    }

    // Password validation
    if (!formData.password) {
      errors.password = t('validation.passwordRequired');
    } else if (formData.password.length < 8) {
      errors.password = t('validation.passwordMinLength');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = t('validation.passwordComplexity');
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('validation.passwordsDoNotMatch');
    }

    // Name validation
    if (!formData.first_name.trim()) {
      errors.first_name = t('validation.firstNameRequired');
    }
    
    if (!formData.last_name.trim()) {
      errors.last_name = t('validation.lastNameRequired');
    }

    // Phone validation (optional but format check if provided)
    if (formData.phone && !/^\+?[-()\d\s]+$/.test(formData.phone)) {
      errors.phone = t('validation.phoneInvalidFormat');
    }

    // Email validation for QR code invitations
    if (isQRCodeInvitation) {
      if (!formData.email.trim()) {
        errors.email = t('validation.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = t('validation.emailInvalidFormat');
      }
    }

    // Entity validation for create_entity invitations
    if (invitation?.invitationType === 'create_entity') {
      if (!formData.entityName.trim()) {
        errors.entityName = t('validation.organizationNameRequired');
      } else if (formData.entityName.length < 3) {
        errors.entityName = t('validation.organizationNameMinLength');
      }
      if (!isListedTimeZoneOption(timezones, formData.timeZone)) {
        errors.timeZone = t('validation.timezoneRequired');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle existing user entity join
   */
  const handleExistingUserJoin = async (password) => {
    setSubmitting(true);

    try {
      const response = await axios.post(
        '/api/users/join-entity-with-invite',
        {
          inviteToken,
          password,
          email: formData.email // Include email for generic invitations
        },
        { skipAuthSessionHandling: true }
      );

      if (response.data.success) {
        const { token, user, entity } = response.data.data;
        
        // Store authentication data
        localStorage.setItem('token', token);
        localStorage.setItem('username', user.username);
        localStorage.setItem('entityId', entity.id);
        localStorage.setItem('entityName', entity.name);
        
        // Set token for immediate use
        setToken(token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        toast.success(t('welcomeBackJoinedEntity', { entityName: entity.name, role: entity.role }));

        // Redirect to dashboard
        navigate('/');
      } else {
        toast.error(response.data.message || t('failedToJoinEntity'));
      }
    } catch (error) {
      console.error('Join entity error', getErrorLogSummary(error));
      
      if (error.response?.data?.error) {
        switch (error.response.data.error) {
          case 'Invalid password':
          case 'INVALID_CREDENTIALS':
            toast.error(t('invalidPasswordTryAgain'));
            break;
          case 'ALREADY_MEMBER':
            toast.error(t('alreadyMemberOrganization'));
            break;
          case 'INVITE_NOT_FOUND':
          case 'INVITE_EXPIRED':
          case 'INVITE_ALREADY_USED':
          case 'USAGE_LIMIT_EXCEEDED':
            handleValidationError(error.response.data);
            break;
          default:
            toast.error(error.response.data.error);
        }
      } else {
        toast.error(t('failedToJoinEntityTryAgain'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle canceling existing user flow (user says "Not me")
   */
  const handleCancelExistingUser = () => {
    setShowExistingUserFlow(false);
    setExistingUser(null);
  };

  const handleExistingAccountClick = () => {
    if (!isQRCodeInvitation) {
      navigate('/login');
      return;
    }

    const email = formData.email.trim();
    if (!email) {
      setValidationErrors(prev => ({
        ...prev,
        email: t('validation.emailRequired')
      }));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setValidationErrors(prev => ({
        ...prev,
        email: t('validation.emailInvalidFormat')
      }));
      return;
    }

    setExistingUser({ email });
    setShowExistingUserFlow(true);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t('pleaseFixFormErrors'));
      return;
    }

    setSubmitting(true);

    try {
      const registrationData = {
        inviteToken,
        username: formData.username.trim(),
        password: formData.password,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone.trim() || null
      };

      // For QR code invitations, include the email provided in the form
      if (isQRCodeInvitation && formData.email) {
        registrationData.email = formData.email.trim();
      }

      // Add entity data for create_entity invitations
      if (invitation.invitationType === 'create_entity') {
        registrationData.entityName = formData.entityName.trim();
        registrationData.organizationType = formData.organizationType;
        registrationData.address = formData.address.trim();
        registrationData.city = formData.city.trim();
        registrationData.state = formData.state.trim();
        registrationData.zipCode = formData.zipCode.trim();
        registrationData.country = formData.country;
        registrationData.entityPhone = formData.entityPhone.trim();
        registrationData.timeZone = formData.timeZone;
      }

      const response = await axios.post(
        '/api/users/register-with-invite',
        registrationData,
        { skipAuthSessionHandling: true }
      );

      if (response.data.success) {
        const { token, user, entity } = response.data.data;
        
        // Store authentication data
        localStorage.setItem('token', token);
        localStorage.setItem('username', user.username);
        localStorage.setItem('entityId', entity.id);
        localStorage.setItem('entityName', entity.name);
        
        // Set token for immediate use
        setToken(token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Show success message based on invitation type
        if (invitation.invitationType === 'create_entity') {
          toast.success(t('welcomeCreatedEntityAdmin', { entityName: entity.name }));
        } else {
          toast.success(t('welcomeJoinedEntity', { entityName: entity.name, role: entity.role }));
        }

        // Redirect to dashboard
        navigate('/');
      } else {
        toast.error(response.data.message || t('registrationFailed'));
      }
    } catch (error) {
      console.error('Registration error', getErrorLogSummary(error));
      
      if (error.response?.data?.error) {
        // Handle specific backend errors
        switch (error.response.data.error) {
          case 'Username already exists':
            setValidationErrors({ username: t('usernameAlreadyTaken') });
            break;
          case 'Email already registered':
            toast.error(t('emailAlreadyRegistered'));
            break;
          case 'GENERIC_INVITE_ACCOUNT_REQUIRED':
            if (isQRCodeInvitation && formData.email.trim()) {
              setExistingUser({ email: formData.email.trim() });
              setShowExistingUserFlow(true);
            } else {
              toast.error(t('emailAlreadyRegistered'));
            }
            break;
          case 'INVALID_ENTITY_TIME_ZONE':
            setValidationErrors(prev => ({
              ...prev,
              timeZone: t('validation.timezoneRequired')
            }));
            break;
          case 'INVITE_NOT_FOUND':
          case 'INVITE_EXPIRED':
          case 'INVITE_ALREADY_USED':
          case 'USAGE_LIMIT_EXCEEDED':
            handleValidationError(error.response.data);
            break;
          default:
            toast.error(error.response.data.error);
        }
      } else {
        toast.error(t('registrationFailedTryAgain'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="absolute right-4 top-4">
          <AuthLanguageSelector />
        </div>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t('validatingInvitation')}</p>
        </div>
      </div>
    );
  }


  // No invitation data
  if (!invitation) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6">
        <div className="flex justify-end">
          <AuthLanguageSelector />
        </div>
        {/* Show ExistingUserJoin component for existing users */}
        {showExistingUserFlow ? (
          <ExistingUserJoin
            existingUser={existingUser}
            invitation={invitation}
            email={formData.email}
            onJoinEntity={handleExistingUserJoin}
            submitting={submitting}
            onCancel={handleCancelExistingUser}
          />
        ) : (
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
            {invitation.invitationType === 'create_entity' 
              ? t('createYourOrganization')
              : t('joinEntityTitle', { entityName: invitation.entityName })}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {invitation.invitationType === 'create_entity'
              ? t('invitedToCreateOrganization', { inviterName: invitation.inviterName })
              : t('invitedBy', { inviterName: invitation.inviterName })}
          </p>
        </div>

        {/* Invitation Details Card */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                {t('invitationDetails')}
              </h3>
              <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                {invitation.invitationType === 'create_entity' ? (
                  <>
                    <p><strong>{t('type')}:</strong> {t('createNewOrganizationLabel')}</p>
                    <p><strong>{t('yourRole')}:</strong> {invitation.role} ({t('organizationAdmin')})</p>
                    <p><strong>{t('email')}:</strong> {invitation.email}</p>
                  </>
                ) : (
                  <>
                    <p><strong>{t('organization')}:</strong> {invitation.entityName}</p>
                    <p><strong>{t('role')}:</strong> {invitation.role}</p>
                    <p><strong>{t('email')}:</strong> {invitation.email}</p>
                  </>
                )}
                {invitation.invitationMessage && (
                  <p><strong>{t('message')}:</strong> {invitation.invitationMessage}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Registration Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">

            {/* Email (for QR code or SMS invitations) */}
            {isQRCodeInvitation && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('emailAddress')} *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                    validationErrors.email ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  placeholder={t('enterEmailPlaceholder')}
                />
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                )}
              </div>
            )}

            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('username')} *
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  validationErrors.username ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                placeholder={t('chooseUsernamePlaceholder')}
              />
              {validationErrors.username && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.username}</p>
              )}
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('firstName')} *
              </label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                required
                value={formData.first_name}
                onChange={handleChange}
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  validationErrors.first_name ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                placeholder={t('firstNamePlaceholder')}
              />
              {validationErrors.first_name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.first_name}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('lastName')} *
              </label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                required
                value={formData.last_name}
                onChange={handleChange}
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  validationErrors.last_name ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                placeholder={t('lastNamePlaceholder')}
              />
              {validationErrors.last_name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.last_name}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('password')} *
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPasswords.password ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 pr-10 border ${
                    validationErrors.password ? 'border-red-300' : 'border-gray-300'
                  } rounded-md focus:ring-blue-500 focus:border-blue-500 placeholder-gray-500 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  placeholder={t('passwordPlaceholder')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPasswords(prev => ({ ...prev, password: !prev.password }))}
                >
                  {showPasswords.password ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
              {validationErrors.password && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('passwordRequirements')}</p>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('confirmPassword')} *
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPasswords.confirmPassword ? "text" : "password"}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 pr-10 border ${
                    validationErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  } rounded-md focus:ring-blue-500 focus:border-blue-500 placeholder-gray-500 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  placeholder={t('confirmPasswordPlaceholder')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirmPassword: !prev.confirmPassword }))}
                >
                  {showPasswords.confirmPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
              {validationErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.confirmPassword}</p>
              )}
            </div>

            {/* Phone (Optional) */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('phoneNumberOptional')}
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  validationErrors.phone ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                placeholder={t('phonePlaceholder')}
              />
              {validationErrors.phone && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('phoneChargeWarning')}
              </p>
              {/* International phone warning — informational only, does not block registration */}
              {formData.phone && isInternationalNumber(formData.phone) && (
                <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{t('smsInternationalInfo')}</span>
                </div>
              )}
            </div>

            {/* Entity Creation Fields - Only show for create_entity invitations */}
            {invitation.invitationType === 'create_entity' && (
              <>
                {/* Section Divider */}
                <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-600">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {t('organizationDetails')}
                  </h3>
                </div>

                {/* Organization Name */}
                <div>
                  <label htmlFor="entityName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('organizationName')} *
                  </label>
                  <input
                    id="entityName"
                    name="entityName"
                    type="text"
                    required={invitation.invitationType === 'create_entity'}
                    value={formData.entityName}
                    onChange={handleChange}
                    className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                      validationErrors.entityName ? 'border-red-300' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                    placeholder={t('organizationNamePlaceholder')}
                  />
                  {validationErrors.entityName && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.entityName}</p>
                  )}
                </div>

                {/* Organization Type */}
                <div>
                  <label htmlFor="organizationType" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('organizationType')} *
                  </label>
                  <select
                    id="organizationType"
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="business">{t('company')}</option>
                    <option value="school">{t('school')}</option>
                    <option value="hotel">{t('hotel')}</option>
                    <option value="healthcare">{t('healthcare')}</option>
                    <option value="government">{t('government')}</option>
                    <option value="nonprofit">{t('nonprofit')}</option>
                    <option value="other">{t('other')}</option>
                  </select>
                </div>

                {/* Address */}
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('address')}
                  </label>
                  <input
                    id="address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder={t('addressPlaceholder')}
                  />
                </div>

                {/* City and State */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('city')}
                    </label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      value={formData.city}
                      onChange={handleChange}
                      className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder={t('cityPlaceholder')}
                    />
                  </div>
                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('stateProvince')}
                    </label>
                    <input
                      id="state"
                      name="state"
                      type="text"
                      value={formData.state}
                      onChange={handleChange}
                      className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder={t('statePlaceholder')}
                    />
                  </div>
                </div>

                {/* Zip Code and Country */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('zipPostalCode')}
                    </label>
                    <input
                      id="zipCode"
                      name="zipCode"
                      type="text"
                      value={formData.zipCode}
                      onChange={handleChange}
                      className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder={t('postalCodePlaceholder')}
                    />
                  </div>
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('country')}
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      disabled={loadingReferenceData}
                    >
                      {loadingReferenceData ? (
                        <option value="">{t('loadingCountries')}</option>
                      ) : (
                        countries.map(country => (
                          <option key={country.iso_code} value={country.iso_code}>
                            {country.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {/* Time Zone */}
                <div>
                  <label htmlFor="timeZone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('timezone')} *
                  </label>
                  <select
                    id="timeZone"
                    name="timeZone"
                    value={formData.timeZone}
                    onChange={handleChange}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white ${validationErrors.timeZone ? 'border-red-300' : 'border-gray-300'}`}
                    disabled={loadingReferenceData || timezones.length === 0}
                  >
                    {loadingReferenceData ? (
                      <option value="">{t('loadingTimezones')}</option>
                    ) : (
                      <>
                        <option value="">{t('selectTimezone')}</option>
                        {timezones.map(timezone => (
                          <option key={timezone.iana_name} value={timezone.iana_name}>
                            {timezone.display_name} ({timezone.utc_offset})
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {validationErrors.timeZone && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.timeZone}</p>
                  )}
                </div>

                {/* Organization Phone */}
                <div>
                  <label htmlFor="entityPhone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('organizationPhone')}
                  </label>
                  <input
                    id="entityPhone"
                    name="entityPhone"
                    type="tel"
                    value={formData.entityPhone}
                    onChange={handleChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder={t('phonePlaceholder')}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('phoneChargeWarning')}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={submitting}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                submitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('creatingAccount')}
                </>
              ) : (
                t('completeRegistration')
              )}
            </button>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('alreadyHaveAccount')}{' '}
              <button
                type="button"
                onClick={handleExistingAccountClick}
                className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
              >
                {t('signInHere')}
              </button>
            </p>
          </div>
        </form>
      </div>
        )}
      </div>
    </div>
  );
};

export default InviteRegistration;
